import { useQuery } from '@tanstack/react-query';
import { toIsoInstant, type AirshipClass, type AirshipWithAvailability } from '@swi/contracts';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import * as api from '../api/endpoints';
import { PageHeader } from '../components/layout/PageHeader';
import {
  Card,
  EmptyState,
  Field,
  Input,
  Pill,
  Select,
  Skeleton,
  buttonClass,
} from '../components/ui/primitives';
import { money, toLocalInputValue } from '../lib/format';

/** Catalogue of the fleet with filters, including availability in a chosen window. */
export function FleetPage() {
  const [hub, setHub] = useState('');
  const [airshipClass, setAirshipClass] = useState<AirshipClass | ''>('');
  const [minCapacity, setMinCapacity] = useState('');
  const [maxRate, setMaxRate] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const hubs = useQuery({ queryKey: ['hubs'], queryFn: api.listHubs });

  const window = useMemo(() => {
    if (from === '' || to === '') {
      return null;
    }
    const start = new Date(from);
    const end = new Date(to);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
      return null;
    }
    return { from: toIsoInstant(start), to: toIsoInstant(end) };
  }, [from, to]);

  const airships = useQuery({
    queryKey: ['airships', { hub, airshipClass, minCapacity, maxRate, window }],
    queryFn: () =>
      api.listAirships({
        hub: hub === '' ? undefined : hub,
        airshipClass: airshipClass === '' ? undefined : airshipClass,
        minCapacity: minCapacity === '' ? undefined : Number(minCapacity),
        maxHourlyRate: maxRate === '' ? undefined : Number(maxRate),
        from: window?.from,
        to: window?.to,
      }),
  });

  return (
    <>
      <PageHeader
        title="Flotila"
        description="Vyber vzducholoď podle hubu, kapacity, třídy a ceny. Zadej termín a uvidíš, která je v něm volná."
        actions={
          <Link to="/reservations/new" className={buttonClass('primary', 'sm')}>
            Nová rezervace
          </Link>
        }
      />

      <Card className="mb-5">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
          <Field label="Hub">
            <Select value={hub} onChange={(event) => setHub(event.target.value)}>
              <option value="">Všechny</option>
              {hubs.data?.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.code} — {item.city}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Třída">
            <Select
              value={airshipClass}
              onChange={(event) => setAirshipClass(event.target.value as AirshipClass | '')}
            >
              <option value="">Všechny</option>
              <option value="A">A — malé</option>
              <option value="B">B — střední</option>
              <option value="C">C — velké</option>
            </Select>
          </Field>

          <Field label="Min. kapacita">
            <Input
              type="number"
              min={1}
              value={minCapacity}
              onChange={(event) => setMinCapacity(event.target.value)}
              placeholder="např. 10"
            />
          </Field>

          <Field label="Max. sazba / h">
            <Input
              type="number"
              min={0}
              step={500}
              value={maxRate}
              onChange={(event) => setMaxRate(event.target.value)}
              placeholder="CZK"
            />
          </Field>

          <Field label="Od" hint={window === null ? 'pro test dostupnosti' : undefined}>
            <Input
              type="datetime-local"
              value={from}
              min={toLocalInputValue(new Date())}
              onChange={(event) => setFrom(event.target.value)}
            />
          </Field>

          <Field label="Do">
            <Input
              type="datetime-local"
              value={to}
              min={from === '' ? toLocalInputValue(new Date()) : from}
              onChange={(event) => setTo(event.target.value)}
            />
          </Field>
        </div>
      </Card>

      {airships.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-60" />
          ))}
        </div>
      ) : (airships.data?.length ?? 0) === 0 ? (
        <EmptyState
          title="Žádná vzducholoď nevyhovuje filtrům"
          description="Zkus rozvolnit kapacitu nebo cenu."
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {airships.data?.map((airship) => (
            <AirshipCard key={airship.id} airship={airship} window={window} />
          ))}
        </div>
      )}
    </>
  );
}

function AirshipCard({
  airship,
  window,
}: {
  airship: AirshipWithAvailability;
  window: { from: string; to: string } | null;
}) {
  const specs: Array<[string, string]> = [
    ['Kapacita', `${String(airship.capacityPax)} pax`],
    ['Cestovní rychlost', `${String(airship.cruiseSpeedKts)} kts`],
    ['Max. vítr', `${String(airship.maxWindSpeedKts)} kts`],
    ['Turnaround', `${String(airship.turnaroundMinutes / 60)} h`],
  ];

  return (
    <Card as="article" className="flex flex-col">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold">{airship.name}</h2>
          <p className="mono text-[12px] text-[var(--accent)]">{airship.registration}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Pill tone={airship.status === 'ACTIVE' ? 'accent' : 'warn'}>
            {airship.status === 'ACTIVE' ? 'v provozu' : 'servis'}
          </Pill>
          <Pill tone="violet">třída {airship.airshipClass}</Pill>
        </div>
      </div>

      <p className="mb-3 line-clamp-2 text-[13px] text-[var(--text-secondary)]">
        {airship.description ?? airship.model}
      </p>

      <dl className="mb-3 grid grid-cols-2 gap-1.5 text-[12px]">
        {specs.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-2">
            <dt className="text-[var(--text-muted)]">{label}</dt>
            <dd className="mono">{value}</dd>
          </div>
        ))}
      </dl>

      <div className="mt-auto flex items-end justify-between gap-2 border-t border-[var(--border-subtle)] pt-3">
        <div>
          <p className="label-caps">Sazba</p>
          <p className="mono text-[15px] font-semibold">
            {money(airship.hourlyRateCzk)}
            <span className="text-[11px] font-normal text-[var(--text-muted)]"> / h</span>
          </p>
          <p className="text-[11px] text-[var(--text-muted)]">
            domovský hub {airship.homeHub.code} · {airship.homeHub.city}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          {window !== null && airship.availableInWindow !== null && (
            <Pill tone={airship.availableInWindow ? 'accent' : 'danger'}>
              {airship.availableInWindow ? 'volná v termínu' : 'obsazená'}
            </Pill>
          )}
          <Link to={`/fleet/${String(airship.id)}`} className={buttonClass('outline', 'sm')}>
            Detail
          </Link>
        </div>
      </div>
    </Card>
  );
}
